import {
  GENERIC_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
} from '@/lib/errors/error-messages';

// Thrown when the BFF/backend doesn't return a parseable JSON response.
export class ClientHttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ClientHttpError';
    this.status = status;
  }
}

/**
 * Thrown when the BFF answered `{ ok: false, code }` — a business failure.
 *
 * Separate from `ClientHttpError` on purpose: transport failure and business
 * failure need different copy, and putting the code into a `message` field is how
 * a stable identifier ends up rendered to the person as-is. The code stays a code;
 * translating it is the caller's job (see `src/lib/host-panel/save-failure.ts`).
 */
export class BffFailure extends Error {
  readonly code: string;

  constructor(code: string) {
    super(`BFF failure: ${code}`);
    this.name = "BffFailure";
    this.code = code;
  }
}

interface SendOptions {
  /** Caller-provided abort signal (composed with the built-in timeout). */
  signal?: AbortSignal;
  /** Override the default 30s browser→BFF timeout (ms). */
  timeoutMs?: number;
}

async function clientSend<T>(
  method: 'POST' | 'PATCH' | 'GET' | 'DELETE',
  path: string,
  body?: unknown,
  options?: SendOptions,
): Promise<T> {
  let response: Response;

  // Compose caller signal with the built-in timeout (30s default).
  const timeoutSignal = AbortSignal.timeout(options?.timeoutMs ?? 30_000);
  const signal = options?.signal
    ? AbortSignal.any([options.signal, timeoutSignal])
    : timeoutSignal;

  try {
    response = await fetch(path, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch {
    throw new ClientHttpError(0, NETWORK_ERROR_MESSAGE);
  }

  // Non-JSON responses (HTML error pages, 502/504 from gateway).
  let json: T;
  try {
    json = (await response.json()) as T;
  } catch {
    throw new ClientHttpError(response.status, GENERIC_ERROR_MESSAGE);
  }

  return json;
}

export async function clientPost<T>(
  path: string,
  body?: unknown,
  options?: SendOptions,
): Promise<T> {
  return clientSend<T>('POST', path, body, options);
}

export async function clientPatch<T>(
  path: string,
  body?: unknown,
  options?: SendOptions,
): Promise<T> {
  return clientSend<T>('PATCH', path, body, options);
}

export async function clientGet<T>(
  path: string,
  options?: SendOptions,
): Promise<T> {
  return clientSend<T>('GET', path, undefined, options);
}

export async function clientDelete<T>(
  path: string,
  options?: SendOptions,
): Promise<T> {
  return clientSend<T>('DELETE', path, undefined, options);
}
